/**
 * Admin Utility Outages — the Island Pulse power/water desk.
 *
 * Domlec and Dowasco have no API and announce outages on Facebook, if at all.
 * An operator reads the post and records it here; the record is what riders see
 * in the app labelled with where it came from.
 *
 * Two rules are enforced by the server and repeated in this UI because getting
 * them wrong could hurt someone:
 *
 *   1. **A restoration time may only be entered if the utility actually gave
 *      one.** The field is disabled unless the source is the utility itself.
 *      Someone on home dialysis may plan around "power back by 4pm"; an
 *      operator's guess is not something to plan around.
 *   2. **An outage must name its villages.** No areas would render island-wide,
 *      which is a far bigger claim than the operator meant.
 *
 * Outages are editable state, unlike `island_alerts` which stay immutable audit
 * rows. Announcing is therefore a separate, explicit action: recording that a
 * village has no water does not wake anyone, and pushing to phones is a
 * decision the operator makes deliberately, once, per update worth waking for.
 *
 * That separation is right and it was also invisible. An operator recorded an
 * outage, expected phones to buzz, and got a toast that scrolled away. So an
 * outage nobody has been told about now READS as unfinished — an amber rail
 * down the row, a "Nobody has been notified" state where the timeline goes,
 * and the Announce button sitting inside that block instead of in the row of
 * secondary actions. Saving also opens the announce step straight away. It is
 * still a separate confirmed send; it is just no longer possible to miss.
 *
 * An announcement can also be withdrawn afterwards from the Island Alerts
 * desk, which leaves a third state: people were pushed earlier and nothing is
 * posted now. It reads as its own state rather than being folded into either
 * neighbour, because "nobody was told" and "a notice is up" are both false of
 * it. What all three turn on is whether an announcement is STILL standing, so
 * every needs-attention decision here keys off `live_announcements` and never
 * off the total.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import OutageAreaSelector, {
  type IslandAreaRow,
} from '@/components/admin/OutageAreaSelector';
import {
  AlertTriangle,
  Ban,
  BellOff,
  CheckCircle2,
  Droplets,
  Info,
  Megaphone,
  Pencil,
  Plus,
  RotateCcw,
  Siren,
  Users,
  Zap,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

type Utility = 'power' | 'water' | 'other';
type Severity = 'info' | 'advisory' | 'emergency';
type Source = 'utility_official' | 'hplate_staff';

/** What riders are reporting inside this outage's own villages. */
interface CrowdSignal {
  restored_reporters: number;
  still_out_reporters: number;
  /** Past the corroboration bar, so it is worth an operator's attention. */
  disputed: boolean;
}

interface OutageRow {
  id: string;
  utility: Utility;
  severity: Severity;
  source: Source;
  source_detail: string | null;
  headline: string;
  known_details: string | null;
  restoration_expected_at: string | null;
  started_at: string;
  expires_at: string;
  resolved_at: string | null;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
  updated_by_name: string | null;
  area_ids: string[];
  areas: { name: string; parish: string }[];
  /** Every announcement ever sent for this outage, withdrawn ones included. */
  announcements: number;
  /** Announcements still standing. Zero means nothing is posted right now. */
  live_announcements: number;
  last_announced_at: string | null;
  announced_recipients: number;
  resolution_cycle: number;
  restoration_push_recipients: number;
  crowd: CrowdSignal;
}

const EMPTY_CROWD: CrowdSignal = {
  restored_reporters: 0,
  still_out_reporters: 0,
  disputed: false,
};

const HEADLINE_MAX = 80;
const DETAILS_MAX = 400;

const UTILITIES: { value: Utility; label: string; provider: string }[] = [
  { value: 'power', label: 'Power', provider: 'Domlec' },
  { value: 'water', label: 'Water', provider: 'Dowasco' },
  { value: 'other', label: 'Other', provider: 'Other provider' },
];

const SEVERITIES: { value: Severity; label: string; desc: string }[] = [
  {
    value: 'info',
    label: 'Notice',
    desc: 'Scheduled or short. Nobody needs to change plans.',
  },
  {
    value: 'advisory',
    label: 'Advisory',
    desc: 'Unplanned, hours long. People will want to know.',
  },
  {
    value: 'emergency',
    label: 'Emergency',
    desc: 'Island-scale or dangerous. Suppresses all ads in the app.',
  },
];

const SOURCES: { value: Source; label: string; desc: string }[] = [
  {
    value: 'utility_official',
    label: 'The utility announced it',
    desc: 'A Domlec / Dowasco notice, post or broadcast. Paste where you read it.',
  },
  {
    value: 'hplate_staff',
    label: 'H-Plate staff observed it',
    desc: 'Our own observation. Shown as ours, never as the utility’s word.',
  },
];

/** Local datetime string an <input type="datetime-local"> accepts. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function utilityIcon(u: Utility) {
  if (u === 'power') return <Zap className="w-4 h-4 text-amber-500" />;
  if (u === 'water') return <Droplets className="w-4 h-4 text-sky-500" />;
  return <Info className="w-4 h-4 text-gray-500" />;
}

function severityBadge(s: Severity) {
  if (s === 'emergency') {
    return (
      <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30">
        Emergency
      </Badge>
    );
  }
  if (s === 'advisory') {
    return (
      <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30">
        Advisory
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
      Notice
    </Badge>
  );
}

function sourceBadge(s: Source, detail: string | null) {
  const label = s === 'utility_official' ? 'Utility announced' : 'H-Plate staff';
  return (
    <span title={detail ?? undefined}>
      <Badge
        variant="outline"
        className={
          s === 'utility_official'
            ? 'border-sky-500/40 text-sky-600 dark:text-sky-400'
            : 'border-gray-500/40 text-gray-500'
        }
      >
        {label}
      </Badge>
    </span>
  );
}

const RPC_ERRORS: Record<string, string> = {
  not_authorized: 'You do not have admin rights on this account.',
  no_valid_areas:
    'Pick at least one village. An outage with no villages would read as the whole island.',
  restoration_requires_official_source:
    'A restoration time can only be recorded when the utility itself gave one. Change the source, or clear the time.',
  invalid_headline: 'The headline must be 1–80 characters.',
  details_too_long: 'Details must be 400 characters or fewer.',
  not_found_or_already_closed: 'That outage was already closed by someone else.',
  not_found_or_still_open: 'That outage is already open.',
  rate_limited:
    'Safety ceiling reached (30 alerts in the last hour). Wait a few minutes.',
};

function explain(code: string | undefined): string {
  if (!code) return 'Unknown error';
  return RPC_ERRORS[code] ?? code;
}

interface FormState {
  id: string | null;
  utility: Utility;
  severity: Severity;
  source: Source;
  sourceDetail: string;
  headline: string;
  knownDetails: string;
  restoration: string;
  ttlHours: number;
  areaIds: string[];
}

const EMPTY_FORM: FormState = {
  id: null,
  utility: 'power',
  severity: 'advisory',
  source: 'utility_official',
  sourceDetail: '',
  headline: '',
  knownDetails: '',
  restoration: '',
  ttlHours: 24,
  areaIds: [],
};

/** Operator desk for recording, editing, announcing and closing outages. */
export default function UtilityOutages() {
  const [areas, setAreas] = useState<IslandAreaRow[]>([]);
  const [rows, setRows] = useState<OutageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  const [announce, setAnnounce] = useState<OutageRow | null>(null);
  const [announceBody, setAnnounceBody] = useState('');
  const [announceSending, setAnnounceSending] = useState(false);

  const areaNames = useMemo(() => {
    const map = new Map<string, IslandAreaRow>();
    for (const a of areas) map.set(a.id, a);
    return map;
  }, [areas]);

  const loadAreas = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_island_areas');
    if (error) {
      toast.error('Could not load the village list.');
      return;
    }
    setAreas(Array.isArray(data) ? (data as IslandAreaRow[]) : []);
  }, []);

  /** Reloads and returns the rows, so callers can act on a row they just saved. */
  const load = useCallback(async (): Promise<OutageRow[]> => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_list_utility_outages', {
        p_include_closed: includeClosed,
        p_limit: 60,
      });
      if (error) throw error;
      const result = data as
        | { ok: boolean; outages?: OutageRow[]; error?: string }
        | null;
      if (!result?.ok) throw new Error(explain(result?.error));
      const next = (result.outages ?? []).map((r) => ({
        ...r,
        crowd: r.crowd ?? EMPTY_CROWD,
        // Covers the deploy window where this bundle is live against a
        // database that predates `live_announcements`. Falling back to the
        // total gives the older, less precise reading instead of making the
        // nothing-is-posted warning disappear altogether.
        live_announcements: r.live_announcements ?? r.announcements,
      }));
      setRows(next);
      return next;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load outages');
      setRows([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [includeClosed]);

  useEffect(() => {
    void loadAreas();
  }, [loadAreas]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Open the announce step for a row, with a sensible first draft. */
  const openAnnounce = useCallback((row: OutageRow) => {
    setAnnounce(row);
    setAnnounceBody(
      row.known_details ??
        `${row.utility === 'water' ? 'Water' : 'Power'} is out in ${row.areas
          .map((a) => a.name)
          .join(', ')}.`,
    );
  }, []);

  const save = useCallback(async () => {
    if (!form) return;
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('admin_upsert_utility_outage', {
        p_utility: form.utility,
        p_severity: form.severity,
        p_source: form.source,
        p_headline: form.headline.trim(),
        p_area_ids: form.areaIds,
        p_known_details: form.knownDetails.trim() || null,
        p_source_detail: form.sourceDetail.trim() || null,
        p_restoration_expected_at: form.restoration
          ? new Date(form.restoration).toISOString()
          : null,
        p_ttl_hours: form.ttlHours,
        p_id: form.id,
      });
      if (error) throw error;
      const result = data as
        | { ok: boolean; outage_id?: string; error?: string }
        | null;
      if (!result?.ok) throw new Error(explain(result?.error));
      toast.success(
        form.id
          ? 'Outage updated. Riders see the change on their next refresh.'
          : 'Outage recorded.',
      );
      const savedId = form.id ?? result.outage_id ?? null;
      setForm(null);
      const next = await load();
      // Recording still does not push — but the announce step is now the
      // screen in front of the operator rather than a toast they can walk
      // away from. They still have to write the message and confirm it.
      const saved = next.find((r) => r.id === savedId);
      // Deliberately the total and not `live_announcements`: this step exists
      // for the operator who does not yet know that recording is not
      // announcing. Someone who announced and then withdrew has proved they
      // know, and pulling a notice is a decision — putting a pre-filled,
      // unrecallable push in front of them every time they touch the row
      // pushes against that decision. The row and the banner still say the
      // outage has nothing posted, and Announce is one click away.
      if (saved && saved.announcements === 0 && !saved.resolved_at && !saved.canceled_at) {
        openAnnounce(saved);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save outage');
    } finally {
      setSaving(false);
    }
  }, [form, load, openAnnounce]);

  const close = useCallback(
    async (row: OutageRow, reason: 'resolved' | 'canceled') => {
      setBusyId(row.id);
      try {
        const { data, error } = await supabase.rpc('admin_close_utility_outage', {
          p_id: row.id,
          p_reason: reason,
        });
        if (error) throw error;
        const result = data as
          | { ok: boolean; restoration_pushes?: number; error?: string }
          | null;
        if (!result?.ok) throw new Error(explain(result?.error));
        if (reason === 'canceled') {
          toast.success('Withdrawn. It will not show as an all-clear.');
        } else {
          const sent = result.restoration_pushes ?? 0;
          // The all-clear goes to whoever a standing announcement reached, so
          // with none standing there is no queue to hold and saying otherwise
          // would have the operator believe the loop was closed.
          toast.success(
            row.announcements === 0
              ? 'Marked restored. Nobody was told it was out, so nobody was told it is back.'
              : row.live_announcements === 0
                ? 'Marked restored. The announcement for it was withdrawn, so there was nobody to send an all-clear to.'
                : sent > 0
                  ? `Marked restored. ${sent} ${sent === 1 ? 'person' : 'people'} told.`
                  : 'Marked restored. The all-clear is held until waking hours for anyone still asleep.',
          );
        }
        await load();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to close outage');
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const reopen = useCallback(
    async (row: OutageRow) => {
      setBusyId(row.id);
      try {
        const { data, error } = await supabase.rpc('admin_reopen_utility_outage', {
          p_id: row.id,
          p_ttl_hours: 24,
        });
        if (error) throw error;
        const result = data as { ok: boolean; error?: string } | null;
        if (!result?.ok) throw new Error(explain(result?.error));
        toast.success('Reopened. Announce again if people should be told.');
        await load();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to reopen outage');
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const sendAnnouncement = useCallback(async () => {
    if (!announce) return;
    setAnnounceSending(true);
    try {
      const { data, error } = await supabase.rpc('send_island_alert', {
        p_severity: announce.severity,
        // Recorded for the audit row only — the area list selects recipients.
        p_audience: 'on_island',
        p_title: announce.headline,
        p_body: announceBody.trim(),
        p_ttl_hours: 12,
        p_area_ids: announce.area_ids,
        p_utility: announce.utility,
        p_source: announce.source,
        p_source_detail: announce.source_detail,
        p_restoration_expected_at: announce.restoration_expected_at,
        p_outage_id: announce.id,
        p_include_adjacent: false,
      });
      if (error) throw error;
      const result = data as
        | { ok: boolean; recipients?: number; error?: string }
        | null;
      if (!result?.ok) throw new Error(explain(result?.error));
      toast.success(
        `Announced to ${result.recipients ?? 0} phone${
          result.recipients === 1 ? '' : 's'
        } in the affected villages.`,
      );
      setAnnounce(null);
      setAnnounceBody('');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to announce');
    } finally {
      setAnnounceSending(false);
    }
  }, [announce, announceBody, load]);

  const startEdit = (row: OutageRow) => {
    setForm({
      id: row.id,
      utility: row.utility,
      severity: row.severity,
      source: row.source,
      sourceDetail: row.source_detail ?? '',
      headline: row.headline,
      knownDetails: row.known_details ?? '',
      restoration: toLocalInput(row.restoration_expected_at),
      ttlHours: Math.max(
        1,
        Math.round(
          (new Date(row.expires_at).getTime() - Date.now()) / 3_600_000,
        ) || 24,
      ),
      areaIds: row.area_ids,
    });
  };

  const statusOf = (r: OutageRow) => {
    if (r.canceled_at) {
      return (
        <Badge variant="outline" className="border-gray-500/30 text-gray-500">
          Withdrawn
        </Badge>
      );
    }
    if (r.resolved_at) {
      return (
        <Badge variant="outline" className="border-emerald-500/30 text-emerald-500">
          Restored
        </Badge>
      );
    }
    if (new Date(r.expires_at).getTime() <= Date.now()) {
      return (
        <Badge variant="outline" className="border-gray-500/30 text-gray-500">
          Lapsed
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30">
        Live
      </Badge>
    );
  };

  const canSave =
    !!form &&
    form.headline.trim().length > 0 &&
    form.areaIds.length > 0 &&
    !saving;

  const isOpen = (r: OutageRow) =>
    !r.resolved_at && !r.canceled_at && new Date(r.expires_at).getTime() > Date.now();

  const unpostedRows = rows.filter((r) => isOpen(r) && r.live_announcements === 0);
  const disputedRows = rows.filter((r) => isOpen(r) && r.crowd.disputed);

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            Utility Outages
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Power and water outages shown in Island Pulse. Recording an outage
            does not notify anyone. Announce separately when it is worth waking
            people for. Crowd reports from riders appear alongside these,
            labelled as user-reported.
          </p>
        </div>
        <Button onClick={() => setForm({ ...EMPTY_FORM })}>
          <Plus className="w-4 h-4 mr-1.5" />
          Record an outage
        </Button>
      </header>

      {unpostedRows.length > 0 ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3 flex-wrap">
          <BellOff className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-amber-800 dark:text-amber-300">
              {unpostedRows.length === 1
                ? '1 live outage has no announcement posted'
                : `${unpostedRows.length} live outages have no announcement posted`}
            </p>
            {/* Wording has to hold for both an outage nobody was ever told
                about and one whose announcement was pulled, so it states what
                is true of both — nothing is reaching anyone now. */}
            <p className="text-sm text-amber-700/80 dark:text-amber-400/80 mt-0.5">
              Nothing is posted for {unpostedRows.map((r) => r.headline).join(' · ')}.
              Recording an outage does not announce it, and a withdrawn
              announcement no longer reaches anyone.
            </p>
          </div>
          {unpostedRows.length === 1 ? (
            <Button onClick={() => openAnnounce(unpostedRows[0])}>
              <Megaphone className="w-4 h-4 mr-1.5" />
              Announce now
            </Button>
          ) : null}
        </div>
      ) : null}

      {disputedRows.length > 0 ? (
        <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-4 flex items-start gap-3">
          <Users className="w-5 h-5 text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sky-800 dark:text-sky-300">
              {disputedRows.length === 1
                ? 'Riders say 1 outage is over'
                : `Riders say ${disputedRows.length} outages are over`}
            </p>
            <p className="text-sm text-sky-700/80 dark:text-sky-400/80 mt-0.5">
              Enough people have reported service back to be worth checking.
              Rider reports never close an outage on their own. Confirm it,
              then mark it restored.
            </p>
          </div>
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <p className="text-sm font-semibold">
              {includeClosed ? 'All outages' : 'Live outages'}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIncludeClosed((v) => !v)}
              >
                {includeClosed ? 'Live only' : 'Show closed'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void load()}
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No {includeClosed ? '' : 'live '}outages recorded.</p>
            </div>
          ) : (
            <ul className="divide-y">
              {rows.map((r) => {
                const open =
                  !r.resolved_at &&
                  !r.canceled_at &&
                  new Date(r.expires_at).getTime() > Date.now();
                const unposted = open && r.live_announcements === 0;
                const withdrawn = unposted && r.announcements > 0;
                return (
                  <li
                    key={r.id}
                    className={
                      unposted
                        ? 'p-4 border-l-4 border-l-amber-500 bg-amber-500/[0.06]'
                        : 'p-4'
                    }
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {utilityIcon(r.utility)}
                          <p className="font-semibold">{r.headline}</p>
                          {statusOf(r)}
                          {unposted ? (
                            <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40">
                              {withdrawn ? 'Announcement withdrawn' : 'Not announced'}
                            </Badge>
                          ) : null}
                          {severityBadge(r.severity)}
                          {sourceBadge(r.source, r.source_detail)}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {r.areas.map((a) => a.name).join(', ')}
                        </p>
                        {r.known_details ? (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5">
                            {r.known_details}
                          </p>
                        ) : null}
                        <p className="text-[11px] text-gray-400 mt-1.5">
                          {r.restoration_expected_at
                            ? `Utility gave ${new Date(
                                r.restoration_expected_at,
                              ).toLocaleString()} · `
                            : 'No restoration time given · '}
                          started{' '}
                          {formatDistanceToNow(new Date(r.started_at), {
                            addSuffix: true,
                          })}
                          {r.updated_by_name ? ` · by ${r.updated_by_name}` : ''}
                        </p>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(r)}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Edit
                        </Button>
                        {open ? (
                          <>
                            {/* An outage with nothing posted carries its own
                                Announce button inside the amber block below,
                                so this stays the update path for a notice that
                                is actually up. */}
                            {r.live_announcements > 0 ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openAnnounce(r)}
                              >
                                <Megaphone className="w-3.5 h-3.5 mr-1" />
                                Post an update
                              </Button>
                            ) : null}
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyId === r.id}
                              onClick={() => void close(r, 'resolved')}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                              Restored
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={busyId === r.id}
                              onClick={() => void close(r, 'canceled')}
                              title="The record was wrong and should not have been shown"
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              Withdraw
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === r.id}
                            onClick={() => void reopen(r)}
                            title="Service went out again, or this was closed by mistake"
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Reopen
                          </Button>
                        )}
                      </div>
                    </div>

                    {unposted ? (
                      <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-3 flex-wrap">
                        <BellOff className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                            {withdrawn
                              ? 'Announcement withdrawn. Nothing is posted now'
                              : 'Not announced. Nobody has been notified'}
                          </p>
                          <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-0.5">
                            {withdrawn
                              ? `People were told earlier: ${
                                  r.announcements === 1
                                    ? '1 announcement went out and was'
                                    : `${r.announcements} announcements went out and were`
                                } withdrawn, so nothing about this is reaching anyone now. The outage itself is still visible in Island Pulse to anyone who opens the app.`
                              : 'This is visible in Island Pulse to anyone who opens the app, but no phone has buzzed.'}{' '}
                            {withdrawn ? 'Announce again' : 'Announce it'} to
                            reach the{' '}
                            {r.areas.length === 1
                              ? '1 village'
                              : `${r.areas.length} villages`}{' '}
                            above.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => openAnnounce(r)}
                          aria-label={
                            withdrawn
                              ? `Announce ${r.headline} again`
                              : `Announce ${r.headline}`
                          }
                        >
                          <Megaphone className="w-3.5 h-3.5 mr-1" />
                          {withdrawn ? 'Announce again' : 'Announce now'}
                        </Button>
                      </div>
                    ) : r.live_announcements === 0 && r.announcements > 0 ? (
                      // Reached on a closed row, where there is nothing left to
                      // announce but the list still has to say which of the two
                      // happened.
                      <p className="mt-2 text-[11px] text-gray-500 flex items-center gap-1.5">
                        <BellOff className="w-3 h-3" />
                        Announced {r.announcements}× earlier, then withdrawn.
                        Nothing is posted now
                      </p>
                    ) : r.announcements > 0 ? (
                      <p className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                        <Megaphone className="w-3 h-3" />
                        Announced {r.announcements}×
                        {r.last_announced_at
                          ? `, last ${formatDistanceToNow(
                              new Date(r.last_announced_at),
                              { addSuffix: true },
                            )}`
                          : ''}
                        {r.announced_recipients > 0
                          ? ` · ${r.announced_recipients} phone${
                              r.announced_recipients === 1 ? '' : 's'
                            } reached`
                          : ''}
                        {r.restoration_push_recipients > 0
                          ? ` · ${r.restoration_push_recipients} told it was back`
                          : ''}
                      </p>
                    ) : null}

                    {open && r.crowd.disputed ? (
                      <div className="mt-3 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 flex items-start gap-3 flex-wrap">
                        <Users className="w-4 h-4 text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-sky-800 dark:text-sky-300">
                            {r.crowd.restored_reporters}{' '}
                            {r.crowd.restored_reporters === 1
                              ? 'person says'
                              : 'people say'}{' '}
                            service is back
                          </p>
                          <p className="text-xs text-sky-700/80 dark:text-sky-400/80 mt-0.5">
                            Rider reports only. This has not closed the outage
                            and never will on its own.
                            {r.crowd.still_out_reporters > 0
                              ? ` ${r.crowd.still_out_reporters} still report it out.`
                              : ''}{' '}
                            Check with{' '}
                            {r.utility === 'water' ? 'DOWASCO' : 'Domlec'} before
                            closing.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === r.id}
                          onClick={() => void close(r, 'resolved')}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                          Mark restored
                        </Button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ── Record / edit ── */}
      <Dialog open={!!form} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {form?.id ? 'Edit outage' : 'Record an outage'}
            </DialogTitle>
            <DialogDescription>
              Write only what you actually know. Riders see the source label next
              to every line of this.
            </DialogDescription>
          </DialogHeader>

          {form ? (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  1 · Utility
                </p>
                <div className="grid sm:grid-cols-3 gap-2">
                  {UTILITIES.map((u) => (
                    <button
                      key={u.value}
                      type="button"
                      onClick={() => setForm({ ...form, utility: u.value })}
                      className={`text-left rounded-lg border-2 p-3 transition-colors ${
                        form.utility === u.value
                          ? 'border-emerald-500 bg-emerald-500/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 font-semibold text-sm">
                        {utilityIcon(u.value)}
                        {u.label}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{u.provider}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  2 · Where did this come from?
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {SOURCES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          source: s.value,
                          // A restoration time is only ever the utility's to
                          // give, so switching away from them clears it rather
                          // than silently keeping a number we can't stand behind.
                          restoration:
                            s.value === 'utility_official'
                              ? form.restoration
                              : '',
                        })
                      }
                      className={`text-left rounded-lg border-2 p-3 transition-colors ${
                        form.source === s.value
                          ? 'border-emerald-500 bg-emerald-500/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <p className="font-semibold text-sm">{s.label}</p>
                      <p className="text-xs text-gray-500 mt-1 leading-snug">
                        {s.desc}
                      </p>
                    </button>
                  ))}
                </div>
                <Input
                  value={form.sourceDetail}
                  onChange={(e) =>
                    setForm({ ...form, sourceDetail: e.target.value })
                  }
                  placeholder={
                    form.source === 'utility_official'
                      ? 'Where you read it (e.g. Domlec Facebook post, 17 Aug 8:14am)'
                      : 'How we know (e.g. our driver reported lights out in Marigot)'
                  }
                  className="mt-2"
                  aria-label="Source detail"
                />
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  3 · Affected villages
                </p>
                <OutageAreaSelector
                  areas={areas}
                  selected={form.areaIds}
                  onChange={(ids) => setForm({ ...form, areaIds: ids })}
                />
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  4 · How serious
                </p>
                <div className="grid sm:grid-cols-3 gap-2">
                  {SEVERITIES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setForm({ ...form, severity: s.value })}
                      className={`text-left rounded-lg border-2 p-3 transition-colors ${
                        form.severity === s.value
                          ? 'border-emerald-500 bg-emerald-500/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <p className="font-semibold text-sm">{s.label}</p>
                      <p className="text-xs text-gray-500 mt-1 leading-snug">
                        {s.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  5 · What is known
                </p>
                <Input
                  value={form.headline}
                  maxLength={HEADLINE_MAX}
                  onChange={(e) =>
                    setForm({ ...form, headline: e.target.value })
                  }
                  placeholder="Headline (e.g. Power out in Portsmouth and Picard)"
                  aria-label="Headline"
                />
                <p className="text-[11px] text-gray-400 mt-1 text-right">
                  {form.headline.length}/{HEADLINE_MAX}
                </p>
                <Textarea
                  value={form.knownDetails}
                  maxLength={DETAILS_MAX}
                  rows={3}
                  onChange={(e) =>
                    setForm({ ...form, knownDetails: e.target.value })
                  }
                  placeholder="Details, in the utility's own words where possible. Leave out anything you are guessing."
                  aria-label="Known details"
                />
                <p className="text-[11px] text-gray-400 mt-1 text-right">
                  {form.knownDetails.length}/{DETAILS_MAX}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">
                    6 · Official restoration time
                  </p>
                  <Input
                    type="datetime-local"
                    value={form.restoration}
                    disabled={form.source !== 'utility_official'}
                    onChange={(e) =>
                      setForm({ ...form, restoration: e.target.value })
                    }
                    aria-label="Official restoration time"
                  />
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1 leading-snug">
                    {form.source === 'utility_official'
                      ? 'Only fill this if the utility stated a time. Leave it empty otherwise, and the app says no time was given, which is the truth. Someone on medical equipment may plan around this.'
                      : 'Disabled: a restoration time can only come from the utility.'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">
                    7 · Keep showing for
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {[6, 12, 24, 48, 72].map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setForm({ ...form, ttlHours: h })}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                          form.ttlHours === h
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                            : 'bg-gray-100 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-700 hover:border-gray-500'
                        }`}
                      >
                        {h}h
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1 leading-snug">
                    A safety net so a forgotten outage stops claiming a village
                    has no water forever. Mark it Restored when service returns.
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setForm(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button disabled={!canSave} onClick={() => void save()}>
              {saving ? 'Saving…' : form?.id ? 'Save changes' : 'Record outage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Announce ── */}
      <Dialog
        open={!!announce}
        onOpenChange={(open) => {
          if (!open) {
            setAnnounce(null);
            setAnnounceBody('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {announce?.severity === 'emergency' ? (
                <Siren className="w-5 h-5 text-red-500" />
              ) : (
                <Megaphone className="w-5 h-5 text-amber-500" />
              )}
              Announce to the affected villages
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                {announce && announce.live_announcements === 0 ? (
                  <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-800 dark:text-amber-300">
                    {announce.announcements === 0
                      ? 'Nobody has been notified about this outage yet. Sending is the step that reaches phones.'
                      : 'The earlier announcement for this outage was withdrawn, so nothing is posted now. Sending posts a new one.'}
                  </p>
                ) : null}
                <p className="text-xs text-gray-500">
                  Goes only to phones whose home village or recent location is in{' '}
                  <span className="font-medium">
                    {announce?.areas.map((a) => a.name).join(', ')}
                  </span>
                  {announce
                    ? ` (${announce.area_ids.length} village${
                        announce.area_ids.length === 1 ? '' : 's'
                      })`
                    : ''}
                  . Notices wait for the recipient's waking hours; advisories and
                  emergencies send immediately.
                </p>
                <Textarea
                  value={announceBody}
                  maxLength={DETAILS_MAX}
                  rows={4}
                  onChange={(e) => setAnnounceBody(e.target.value)}
                  aria-label="Announcement message"
                />
                <div className="flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>
                    Pushes cannot be recalled. Do not state a restoration time
                    the utility did not give.
                  </span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAnnounce(null)}
              disabled={announceSending}
            >
              {announce && announce.live_announcements === 0
                ? announce.announcements === 0
                  ? 'Not now, leave it unannounced'
                  : 'Not now, leave nothing posted'
                : 'Back'}
            </Button>
            <Button
              disabled={announceSending || announceBody.trim().length === 0}
              onClick={() => void sendAnnouncement()}
            >
              {announceSending ? 'Sending…' : 'Announce now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {areaNames.size === 0 && !loading ? (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          The village list is empty. Run the Island Pulse migrations before
          recording an outage.
        </p>
      ) : null}
    </div>
  );
}
